import {App, InjectionKey, reactive, readonly} from "vue";
import type {Router} from "vue-router";
import User from "../models/User";
import {useCookies} from "../composables/useCookies";
import {ApplicationType} from "../types/ApplicationType.ts";
import {useMessagesStore} from "../stores/messages.ts";
import Application from "../models/Application.ts";
import api from "../api.ts";

/**
 * Represents a user's access control entry for an application.
 */
interface AccessControlEntry {
    id: string;
    user_id: string;
    created_at: string;
    updated_at: string;
    application: {
        id: string;
        client_id: string;
        secret: string;
        title: string;
        description: string;
        url: string;
        status: boolean;
        type: ApplicationType;
        legacy: boolean;
        created_at: string;
        updated_at: string;
    }
}

/**
 * OAuth redirect parameters structure
 */
interface OAuthParams {
    callbackUrl: string;
    clientId: string;
    code_challenge?: string;
    code_challenge_method?: string;
    state?: string;
    other?: string;
}

/**
 * Represents the authentication state for the GenAuth Portal.
 */
export interface PortalAuthState {
    user: User | null;
    isAuthenticated: boolean;
    token: string | null;
    otpVerified: boolean;
    permissions: AccessControlEntry[];
    oauthRedirect?: OAuthParams;
    ready: boolean;
    authorizationInProgress: boolean;
    redirecting: boolean;
}

/**
 * Defines the methods and reactive state provided by the GenAuth portal plugin.
 */
export interface PortalAuth {
    login: (username: string, password: string) => Promise<void>;
    quickAuthLogin: (qaId: string) => Promise<void>;
    logout: () => Promise<void>;
    checkAuthStatus: () => Promise<void>;
    sendOtpCode: (method: "sms" | "email", token: string) => Promise<boolean>;
    verifyOtpCode: (
        code: string,
        method: "sms" | "email",
        token: string,
        client_id: string,
        callback_url?: string
    ) => Promise<[boolean, string?]>;
    authState: Readonly<PortalAuthState>;
}

/**
 * Injection key for accessing the GenAuth portal plugin via Vue’s dependency injection.
 */
export const PortalAuthKey: InjectionKey<PortalAuth> = Symbol("PortalAuth");

export let portalAuthInstance: PortalAuth;

/**
 * Vue plugin for handling authentication logic within the GenAuth Portal application.
 */
export default {
    /**
     * Installs the GenAuth portal authentication plugin.
     *
     * @param app - The Vue app instance.
     * @param context - Context including router.
     */
    install(app: App, {router}: { router: Router }) {
        const {getCookie, setCookie, removeCookie} = useCookies();
        const messages = useMessagesStore();

        // Initialize reactive auth state
        const authState = reactive<PortalAuthState>({
            user: null,
            isAuthenticated: false,
            token: getCookie("access_token") || null,
            otpVerified: false,
            permissions: [],
            oauthRedirect: undefined,
            ready: false,
            authorizationInProgress: false,
            redirecting: false,
        });

        /**
         * Clears the authentication state by resetting all auth-related state variables
         * and removing the access token cookie. This is used during logout and when
         * authentication checks fail.
         */
        const clearAuthState = () => {
            authState.user = null;
            authState.token = null;
            authState.otpVerified = false;
            authState.isAuthenticated = false;
            authState.permissions = [];
            authState.authorizationInProgress = false;
            authState.redirecting = false;
            removeCookie("access_token");
        };

        /**
         * Sets the authentication state using user data and token.
         *
         * @param user - Raw user object.
         * @param token - Access token.
         * @param otp_verified - OTP verified flag.
         * @param permissions - The applications that the user has access to
         */
        const setAuth = (user: any, token: string, otp_verified: 0 | 1, permissions: any[]) => {
            authState.user = User.fromJson(user);
            authState.token = token;
            authState.otpVerified = otp_verified === 1;
            authState.isAuthenticated = true;
            authState.permissions = permissions;
            setCookie("access_token", token, 1);
        };

        /**
         * Helper method to parse the current OAuth state from query params
         */
        const oauthStateHelper = (): {
            callbackUrl: string | null,
            clientId: string | null,
            code_challenge: string | null,
            code_challenge_method: string | null,
            state: string | null,
            other: string,
        } => {
            const params = new URLSearchParams(window.location.search);

            // Extract predefined parameters
            const callbackUrl = params.get("callbackUrl");
            const clientId = params.get("clientId");
            const code_challenge = params.get("code_challenge");
            const code_challenge_method = params.get("code_challenge_method");
            const state = params.get("state");

            // Collect additional params into "other"
            const otherParams: Record<string, string> = {};
            params.forEach((value, key) => {
                if (!["callbackUrl", "clientId", "code_challenge", "code_challenge_method", "state"].includes(key)) {
                    otherParams[key] = value;
                }
            });

            return {
                callbackUrl,
                clientId,
                code_challenge,
                code_challenge_method,
                state,
                other: JSON.stringify(otherParams)
            }
        }

        /**
         * Gets OAuth parameters from URL query parameters or from stored state.
         * First tries to extract parameters from the current URL, then falls back
         * to previously stored parameters in the auth state.
         *
         * @returns OAuth parameters object or null if no parameters are available
         */
        const getOAuthParams = (): OAuthParams | null => {

            // Extract data from query params
            const {
                callbackUrl,
                clientId,
                code_challenge,
                code_challenge_method,
                state,
                other
            } = oauthStateHelper();

            // First try to get from URL
            if (callbackUrl && clientId) {
                return {
                    callbackUrl,
                    clientId,
                    code_challenge: code_challenge ?? undefined,
                    code_challenge_method: code_challenge_method ?? undefined,
                    state: state ?? undefined,
                    other: other
                };
            }

            // Fall back to stored state
            if (authState.oauthRedirect) {
                return authState.oauthRedirect;
            }

            return null;
        };

        /**
         * Reads OAuth redirect parameters from the current URL and stores them in authState.
         * This function is called during login, quickAuth login, and authentication checks
         * to ensure OAuth parameters are preserved for later use in the authorization flow.
         */
        const persistOAuthParams = () => {

            // Extract data from query params
            const {
                callbackUrl,
                clientId,
                code_challenge,
                code_challenge_method,
                state,
                other
            } = oauthStateHelper();

            if (callbackUrl && clientId) {
                authState.oauthRedirect = {
                    callbackUrl,
                    clientId,
                    code_challenge: code_challenge ?? undefined,
                    code_challenge_method: code_challenge_method ?? undefined,
                    state: state ?? undefined,
                    other: other
                };
            } else {
                // Logs go here is needed for debugging
            }
        };

        /**
         * Clears the current OAuth param state
         */
        const clearOauthParams = (): void => {
            authState.oauthRedirect = undefined;
        }

        /**
         * Requests an authorization code for a specific user, client, and callback URI.
         *
         * @param user_id - The user's ID.
         * @param client_id - The OAuth client ID.
         * @param callback_uri - The redirect URI.
         * @param code_challenge - PKCE code challenge
         * @param code_challenge_method - PKCE code challenge method
         * @returns A Promise resolving to [success, authorizationCode, error].
         */
        const authorize = async (
            user_id: string,
            client_id: string,
            callback_uri: string,
            code_challenge?: string,
            code_challenge_method?: string,
            state?: string,
        ): Promise<[boolean, string, Error?]> => {
            try {
                const response = await api.get("/api/o/authorize?accept=json", {
                    params: {
                        user_id,
                        client_id,
                        redirect_uri: callback_uri,
                        code_challenge,
                        code_challenge_method,
                        state,
                    },
                });

                console.log("authorize", response.data);
                if (response.status === 200) {
                    return [true, response.data.code];
                } else {
                    return [false, "Unauthorized"];
                }
            } catch (error) {
                return [false, "", error as Error];
            }
        };

        /**
         * Checks if the current user has permission to access the specified application.
         * Makes an API call to the backend to verify access permissions.
         *
         * @param callback_url - The callback URL of the application
         * @param client_id - The client ID of the application
         * @returns A Promise resolving to true if the user has permission, false otherwise
         */
        const checkPermissions = async (callback_url: string, client_id: string): Promise<boolean> => {

            try {
                const response = await api.post(
                    "/api/auth/checkperms",
                    {
                        user_id: authState.user?.id,
                        callback_url,
                        client_id,
                    },
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: "Bearer " + authState.token,
                        },
                    }
                );

                return response.status === 200;
            } catch (error: any) {
                return false;
            }
        };

        const isAppLegacy = async (client_id: string): Promise<boolean> => {
            const [success, app, _] = await Application.fetchByClientId(client_id);

            if (success) {
                console.log("app", app);
                return app.legacy === 1;
            } else {
                return false;
            }
        }

        /**
         * Handles OAuth authorization flow and redirect
         *
         * @param userId - The user's ID
         * @param oauth - OAuth parameters
         * @returns [success, errorMessage]
         */
        const handleOAuthFlow = async (
            userId: string,
            oauth: OAuthParams
        ): Promise<[boolean, string?]> => {
            const tokenCookie = useCookies().getCookie("access_token");
            authState.redirecting = false;

            // Prevent multiple simultaneous authorization calls
            if (authState.authorizationInProgress) {
                return [false, "Authorization already in progress"];
            }

            try {
                // Set flag to indicate authorization is in progress
                authState.authorizationInProgress = true;

                // Check permissions
                const allowed = await checkPermissions(oauth.callbackUrl, oauth.clientId);
                if (!allowed) {
                    clearOauthParams();
                    messages.add({
                        text: "You do not have access to the requested application!",
                        color: "red",
                    });
                    return [false, "Access denied"];
                }

                const isLegacy = await isAppLegacy(oauth.clientId);

                // Prepare additional query parameters
                let additionalParams: string = "";
                if (oauth.other) {
                    const otherParams = JSON.parse(oauth.other); // Parse the `other` field
                    additionalParams = Object.entries(otherParams)
                        .map(([key, value]: [string, string]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
                        .join("&");
                }

                const redirectUrl = new URL(oauth.callbackUrl);
                if (oauth.state) {
                    redirectUrl.searchParams.set("state", oauth.state);
                }
                if (additionalParams) {
                    const passthroughParams = new URLSearchParams(additionalParams);
                    passthroughParams.forEach((value, key) => {
                        redirectUrl.searchParams.set(key, value);
                    });
                }

                if (isLegacy) {
                    redirectUrl.searchParams.set("access_token", tokenCookie ?? "");
                    window.location.href = redirectUrl.toString();
                    authState.oauthRedirect = undefined;
                    authState.redirecting = true;
                    return [true];
                } else {
                    // Get authorization code
                    const [ok, authCode, _] = await authorize(
                        userId,
                        oauth.clientId,
                        oauth.callbackUrl,
                        oauth.code_challenge,
                        oauth.code_challenge_method,
                        oauth.state,
                    );

                    if (ok) {
                        console.log("authCode", authCode);
                        redirectUrl.searchParams.set("authorization_code", authCode);
                        redirectUrl.searchParams.set("code", authCode);
                        window.location.href = redirectUrl.toString();

                        console.log('additionalParams', additionalParams);
                        authState.oauthRedirect = undefined;
                        authState.redirecting = true;
                        return [true];
                    } else {
                        return [false, "Authorization failed"];
                    }
                }
            } catch (error) {
                return [false, "Unexpected error in OAuth flow"];
            } finally {
                authState.authorizationInProgress = false;
            }
        };

        /**
         * Axios request interceptor that automatically adds client_id to request bodies.
         * This ensures that all API requests include the client ID when OAuth parameters are available.
         * Only modifies POST, PUT, and PATCH requests with JSON content.
         */
        api.interceptors.request.use((config) => {
            const oauth = getOAuthParams();
            if (!oauth?.clientId) return config;

            const clientId = oauth.clientId;

            // Only inject into body for POST, PUT, PATCH requests
            const method = config.method?.toLowerCase();
            if (["post", "put", "patch"].includes(method || "")) {
                if (config.headers?.["Content-Type"] === "application/json" || !config.headers?.["Content-Type"]) {
                    if (typeof config.data === "string") {
                        try {
                            config.data = JSON.parse(config.data);
                        } catch {
                            config.data = {};
                        }
                    } else if (typeof config.data !== "object" || config.data === null) {
                        config.data = {};
                    }

                    config.data.client_id = clientId;
                }
            }

            return config;
        }, (error) => {
            return Promise.reject(error);
        });

        /**
         * Logs in the user using username and password credentials.
         *
         * @param username - The user's username.
         * @param password - The user's password.
         * @throws An error if login fails.
         */
        const login = async (username: string, password: string) => {
            const credentials = btoa(`${username}:${password}`);
            try {
                const response = await api.post(
                    `/api/auth/login`,
                    {
                        credentials,
                    },
                    {withCredentials: true}
                );

                persistOAuthParams();

                setAuth(response.data.user, response.data.token, response.data.otp_verified, response.data.user.access_controls);
                await routePostLogin(response.data.user, response.data.token, response.data.otp_verified);
            } catch (err) {
                throw new Error(err.response.data.error);
            }
        };

        /**
         * Logs in the user using QuickAuth (e.g., magic link).
         *
         * @param qaId - The QuickAuth ID.
         * @throws An error if authentication fails.
         */
        const quickAuthLogin = async (qaId: string) => {
            persistOAuthParams();
            try {
                const response = await api.post(
                    `/api/auth/qa/login`,
                    {id: qaId},
                    {withCredentials: true}
                );

                setAuth(response.data.user, response.data.token, response.data.otp_verified, response.data.user.access_controls);
                await routePostLogin(response.data.user, response.data.token, response.data.otp_verified);
            } catch (err) {
                throw new Error("QuickAuth login failed");
            }
        };

        /**
         * Logs the user out by clearing auth state and redirecting to login.
         */
        const logout = async () => {
            const token = authState.token;

            if (token) {
                try {
                    await api.post(
                        `/api/auth/logout`,
                        {user_id: authState.user?.id},
                        {
                            withCredentials: true,
                            headers: {Authorization: `Bearer ${token}`},
                        }
                    );
                } catch (err) {
                    // Silently handle logout errors
                    // Logs go here is needed for debugging
                }
            } else {
                // Logs go here is needed for debugging
            }

            clearAuthState();
            await router.push({name: "app.login"});
        };

        /**
         * Checks whether the current user session is still valid and authenticated.
         * Verifies the access token with the server and updates the authentication state accordingly.
         * If authentication is successful and the user is OTP verified, attempts an OAuth redirect.
         * This function is called automatically when the plugin is installed and can be called
         * manually to refresh the authentication state.
         */
        const checkAuthStatus = async () => {
            try {
                const token = getCookie("access_token");
                if (!token) {
                    clearAuthState();
                    return;
                }

                const response = await api.get(`/api/auth/authenticated`, {
                    withCredentials: true,
                    headers: {Authorization: `Bearer ${token}`},
                });

                if (response.data.auth) {
                    setAuth(
                        response.data.user,
                        response.data.token,
                        response.data.otp_verified,
                        response.data.user.access_controls
                    );
                    persistOAuthParams();

                    // Only auto-redirect if already OTP verified
                    if (response.data.otp_verified === 1) {
                        const redirected: boolean = await tryOAuthRedirect(response.data.user);
                        if (redirected) {
                            return;
                        }
                    } else {
                        // Logs go here is needed for debugging
                    }
                } else {
                    clearAuthState();
                }
            } catch (err) {
                // Gracefully fail instead of calling logout
                clearAuthState();
            } finally {
                authState.ready = true;
            }
        };

        /**
         * Sends a one-time password (OTP) code to the user via the selected method.
         *
         * @param method - The delivery method for the OTP code ("sms" or "email")
         * @param token - The access token for authentication
         * @returns A Promise resolving to true if the OTP code was sent successfully, false otherwise
         */
        const sendOtpCode = async (method: "sms" | "email", token: string): Promise<boolean> => {
            if (!authState.user) return false;

            try {
                await api.post(
                    `/api/auth/otp/generate?method=${method}`,
                    {
                        user_id: authState.user.id,
                        phone: authState.user.phone,
                    },
                    {
                        withCredentials: true,
                        headers: {Authorization: `Bearer ${token}`},
                    }
                );
                return true;
            } catch {
                return false;
            }
        };

        /**
         * Verifies the provided OTP code and handles OAuth redirect if applicable.
         * If verification is successful and OAuth parameters are available, initiates the OAuth flow.
         *
         * @param code - The OTP code entered by the user
         * @param method - The delivery method used for the OTP code ("sms" or "email")
         * @param token - The access token for authentication
         * @returns A Promise resolving to [success, errorMessage] where success is true if verification was successful
         */
        const verifyOtpCode = async (
            code: string,
            method: "sms" | "email",
            token: string,
        ): Promise<[boolean, string?]> => {

            try {
                const response = await api.post(
                    `/api/auth/otp/verify?method=${method}`,
                    {
                        phone: authState.user?.phone,
                        code,
                    },
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                if (response.status === 200 && response.data.response === "approved") {
                    authState.otpVerified = true;

                    const oauth = getOAuthParams();
                    if (!oauth?.callbackUrl || !oauth?.clientId) {
                        await router.push({name: "app.landing"});
                        return [true];
                    }

                    const [success, _] = await handleOAuthFlow(response.data.user.id, oauth);
                    if (!success) {
                        await router.push({name: "app.landing"});
                    }
                    return [true];
                }

                return [false, "Invalid or expired code"];
            } catch (error: any) {
                const message = error.response?.data?.error ?? "OTP verification failed";
                return [false, message];
            }
        };

        /**
         * Redirects the user to the appropriate route based on their login status.
         *
         * @param user - The authenticated user object.
         * @param token - The access token.
         * @param otp_verified - Whether the user's OTP has been verified.
         */
        const routePostLogin = async (
            user: any,
            token: string,
            otp_verified: 0 | 1
        ) => {

            // Handle temporary password case
            if (user.user_status?.name === "TEMPORARY_PASSWORD") {
                await router.push({name: "app.temppass"});
                return;
            }

            // Handle OTP verification requirement
            if (otp_verified === 0) {
                const oauth = getOAuthParams();
                const otherQuery = oauth?.other ? JSON.parse(oauth.other) : {};

                await router.push({
                    name: "app.otp",
                    query: {
                        access_token: token,
                        callbackUrl: oauth?.callbackUrl ?? "",
                        clientId: oauth?.clientId ?? "",
                        code_challenge: oauth?.code_challenge ?? "",
                        code_challenge_method: oauth?.code_challenge_method ?? "",
                        state: oauth?.state ?? "",
                        ...otherQuery,
                    },
                });
                return;
            }

            // User is authenticated and OTP verified
            authState.otpVerified = true;

            // Handle OAuth flow if parameters are present
            const oauth = getOAuthParams();
            if (!oauth?.callbackUrl || !oauth?.clientId) {
                await router.push({name: "app.landing"});
                return;
            }

            const [success, _] = await handleOAuthFlow(user.id, oauth);
            if (!success) {
                await router.push({name: "app.landing"});
            }
        };

        /**
         * If the user is authenticated and the CallbackURL includes OAuth params,
         * issue an auth code and redirect back to the client app.
         *
         * @param user - The authenticated user object
         */
        const tryOAuthRedirect = async (user: User): Promise<boolean> => {

            const oauth = getOAuthParams();

            if (!oauth || !authState.otpVerified) {
                return false;
            }

            const [success, _] = await handleOAuthFlow(user.id, oauth);

            return success;
        };

        // Create the auth instance with public methods
        portalAuthInstance = {
            login,
            quickAuthLogin,
            logout,
            checkAuthStatus,
            sendOtpCode,
            verifyOtpCode,
            authState: readonly(authState) as Readonly<PortalAuthState>,
        }

        // Provide the plugin
        app.provide(PortalAuthKey, portalAuthInstance);

        // Initialize auth status check
        checkAuthStatus();
    },
};
